// Copyright 2016 Sorama B.V.

import { BindingEngine, computedFrom, Disposable, inject, observable, transient } from "aurelia-framework";
import { Router } from "aurelia-router";
import { CaptureDevice, MeasurementHealthIssue, MeasurementHealthIssueType } from "datatypes/SoramaLocalApi";
import { IWorkflowModuleLifetime } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { MeasurementSetupRepository } from "repositories/MeasurementSetupRepository";
import { IChannelSelectionPresetService } from "services/ChannelSelection/IChannelSelectionPresetService";
import { ClientStatus, IClientService } from "services/Client/IClientService";
import { CamIV64Service } from "services/Hardware/CamHTTP/CamIV64Service";
import { ConnectedHardwareService } from "services/Hardware/ConnectedHardwareService";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import {
    ConnectedMeasurementDeviceSetup,
    MeasurementDeviceSetupStub,
} from "services/Hardware/Datatypes/MeasurementDeviceSetup";
import { IHardwareService } from "services/Hardware/IHardwareService";
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";
import { MotionDeviceDiscoveryService } from "services/MotionDevice/MotionDeviceDiscoveryService";
import { MotionDeviceService } from "services/MotionDevice/MotionDeviceService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";

/**
 * MODULE OPERATION DESCRIPTION - HardwareSelectionModule
 * The Hardware Selection Module provides a way for the user to select hardware to use for a measurement.
 * Other properties that are controlled in this module are (workflowproperty or router paths):
 * - None as of yet
 */
@transient()
@inject(
    IMeasurementAcquisitionService,
    "IHardwareService<MeasurementDevice>",
    MeasurementSetupRepository,
    IChannelSelectionPresetService,
    IClientService,
    BindingEngine,
    ConnectedHardwareService,
    MotionDeviceDiscoveryService,
    MotionDeviceService,
    Router,
    WorkflowModeService,
    CamIV64Service,
    ShellViewManager
)
export class HardwareSelectionModule implements IWorkflowModuleLifetime {
    //#region Input Properties
    @observable
    private measurementDevice: MeasurementDevice;

    @observable
    private measurementSetup: ConnectedMeasurementDeviceSetup;

    private selectableCaptureDevices: CaptureDevice[];

    @observable
    private selectedCaptureDevice: CaptureDevice;

    @observable
    private selectedMotionDeviceId: string;

    //#endregion

    //#region Workflow properties
    @observable
    public frontEndId: string;

    @observable
    public setupId: string;

    @observable
    public captureDeviceId: string;
    //#endregion

    //#region Computed Properties
    @computedFrom("measurementSetupRepository.items.length", "measurementDeviceService.hardware.length")
    public get availableMeasurementSetups(): MeasurementDeviceSetupStub[] {
        // Filter out all setups which contain empty devices and of which not every device is currently connected
        return this.measurementSetupRepository.items.filter(
            (x) =>
                x.measurementDevices &&
                x.measurementDevices.length > 0 &&
                x.measurementDevices.every(
                    (y) => !!y.id && this.measurementDeviceService.hardware.findIndex((z) => z.id === y.id) != -1
                )
        );
    }
    //#endregion

    //#region Other properties
    private disposables: Disposable[];
    private explanation: string;
    private initialized: boolean;
    private bindPromise: Promise<void>;
    //#endregion

    constructor(
        private readonly measurementAcquisitionService: IMeasurementAcquisitionService,
        private readonly measurementDeviceService: IHardwareService<MeasurementDevice>,
        private readonly measurementSetupRepository: MeasurementSetupRepository,
        private readonly channelSelectionPresetService: IChannelSelectionPresetService,
        private readonly clientService: IClientService,
        private readonly bindingEngine: BindingEngine,
        private readonly connectedHardwareService: ConnectedHardwareService,
        private readonly motionDeviceService: MotionDeviceDiscoveryService,
        private readonly motionService: MotionDeviceService,
        private readonly router: Router,
        private readonly workflowModeService: WorkflowModeService,
        private readonly camIV64Service: CamIV64Service,
        private readonly shellviewManager: ShellViewManager
    ) {}

    //#region Lifecycle functions

    public activateModule(): void {
        this.initialized = false;

        this.disposables = [
            this.bindingEngine
                .propertyObserver(this.measurementDeviceService, "isRefreshing")
                .subscribe(this.isRefreshingChanged),
            this.bindingEngine.propertyObserver(this.clientService, "status").subscribe(this.clientStatusChanged),
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "activeSetup")
                .subscribe(this.activeSetupChanged),
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "issues")
                .subscribe(this.issuesChanged),
            this.bindingEngine
                .propertyObserver(this.motionService, "connectedMotionDevice")
                .subscribe(this.connectedMotionDeviceChanged),
        ];

        this.connectedMotionDeviceChanged();

        let promise: Promise<boolean>;
        if (!this.clientStatusChanged(this.clientService.status, undefined, true)) {
            // Status was not available or out of date, do another check before actually navigating
            promise = this.clientService
                .updateStatus()
                .catch(() => {})
                .then(() => this.clientStatusChanged(this.clientService.status));
        } else {
            promise = Promise.resolve(true);
        }

        this.bindPromise = promise.then((cont) => {
            if (!cont) return; // No need to do this, we are navigating away

            return this.measurementSetupRepository
                .getAll()
                .then((items) => {
                    this.measurementSetupRepository.items = items;
                    this.initialized = true;
                })
                .catch(() => {})
                .then(() => {
                    this.clientStatusChanged(this.clientService.status);
                    this.issuesChanged(this.measurementAcquisitionService.issues);
                    this.activeSetupChanged(this.measurementAcquisitionService.activeSetup);
                });
        });
    }

    public deactivateModule(): void {
        this.bindPromise.then(() => {
            while (this.disposables.length > 0) {
                this.disposables.pop().dispose();
            }
        });
    }

    //#endregion

    //#region Property Changed Callbacks

    private selectedMotionDeviceIdChanged(): void {
        if (!this.selectedMotionDeviceId) {
            this.motionService.connectedMotionDevice = undefined;
            return;
        }

        this.motionDeviceService.getHardwareById(this.selectedMotionDeviceId).then((x) => {
            this.motionService.connectedMotionDevice = x;
        });
    }

    private connectedMotionDeviceChanged = (): void => {
        this.selectedMotionDeviceId = this.motionService.connectedMotionDevice
            ? this.motionService.connectedMotionDevice.id
            : undefined;
    };

    private clientStatusChanged = (
        status: ClientStatus,
        oldStatus?: ClientStatus,
        preventNavigate?: boolean
    ): boolean => {
        if (status !== ClientStatus.NotAvailable && status !== ClientStatus.UnsupportedVersion) {
            // Refresh devices
            this.refreshDevicesAndSetups();
            this.camIV64Service.refresh();

            return true;
        } else {
            return false;
        }
    };

    private isRefreshingChanged = (newValue: boolean): void => {
        if (!newValue && this.frontEndId) {
            // Trigger a front end id update manually
            //this.frontEndIdChanged(this.frontEndId);
        }
    };

    private activeSetupChanged = (newValue: ConnectedMeasurementDeviceSetup): void => {
        if (newValue) {
            const capDevice =
                newValue.captureDevices && newValue.captureDevices.length > 0 ? newValue.captureDevices[0] : undefined;

            this.selectedCaptureDevice = capDevice;
            this.frontEndId =
                newValue.frontEnds && newValue.frontEnds.length === 1 ? newValue.frontEnds[0].id : undefined;
            this.setupId = this.frontEndId ? undefined : newValue.id;
        } else {
            this.frontEndId = undefined;
            this.setupId = undefined;
        }
    };

    private issuesChanged = (newValue: MeasurementHealthIssue[]): void => {
        if (!newValue || newValue.length === 0) {
            this.explanation = undefined;
        } else {
            if (newValue.some((x) => x.type === MeasurementHealthIssueType.InsufficientThroughput)) {
                this.explanation =
                    "The connection between your PC and the sound camera is too slow. This may cause delays when doing live stream measurement and may even interrupt your measurement. This may be caused by the following reasons: - Slow ethernet port on your computer (1 gbit/s ethernet port required) - Slow switch or router between the sound camera and your computer.";
            }
        }
    };

    private frontEndIdChanged(newValue: string, oldValue?: string): void {
        if (!this.initialized) return;

        if (!this.frontEndId) {
            this.measurementDevice = undefined;
            this.disconnectMeasurementSetup();
            this.selectableCaptureDevices = [];
            return;
        }

        // Get measurement device
        const measurementDevice = this.measurementDeviceService.hardware.find((x) => x.id === newValue);
        this.selectableCaptureDevices = measurementDevice ? measurementDevice.captureDevices : [];

        if (measurementDevice) {
            if (
                !this.selectableCaptureDevices.some((x) => x.id == this.captureDeviceId) &&
                !this.measurementAcquisitionService.activeSetup
            ) {
                this.captureDeviceId =
                    this.selectableCaptureDevices.length === 0 ? undefined : this.selectableCaptureDevices[0].id;
            }
        }

        this.measurementDevice = measurementDevice;
    }

    private setupIdChanged(newValue: string, oldValue?: string): void {
        if (!this.initialized) return;

        if (newValue && !this.frontEndId) {
            // Get measurement setup
            this.measurementSetupRepository.getById(newValue).then((setupStub) => {
                return ConnectedMeasurementDeviceSetup.fromConnectedDevices(
                    setupStub,
                    this.measurementDeviceService
                ).then((connectedSetup) => {
                    this.selectableCaptureDevices = connectedSetup.captureDevicesAsInSetup;

                    // Create measurement setup from stub
                    this.measurementSetup = connectedSetup;

                    if (!this.selectableCaptureDevices.some((x) => x.id == this.captureDeviceId)) {
                        this.captureDeviceId =
                            this.selectableCaptureDevices.length === 0
                                ? undefined
                                : this.selectableCaptureDevices[0].id;
                    }
                });
            });
        } else {
            this.measurementSetup = undefined;
        }
    }

    private measurementDeviceChanged(newValue: MeasurementDevice) {
        if (!this.initialized || !this.measurementDevice) return;

        this.setMeasurementSetup();
    }

    private measurementSetupChanged(newValue: ConnectedMeasurementDeviceSetup) {
        if (!this.initialized || !this.measurementSetup) return;

        // Set webcam of setup
        this.setMeasurementSetup();
    }

    private selectedCaptureDeviceChanged(newValue: CaptureDevice): void {
        if (!this.initialized) return;

        this.captureDeviceId = newValue ? newValue.id : undefined;
    }

    private captureDeviceIdChanged(newValue: string): void {
        if (!this.initialized) return;

        if (!this.measurementDevice) return;

        if (this.measurementDevice.captureDevices.some((x) => x.id == this.captureDeviceId) || !newValue) {
            this.setMeasurementSetup();
        }
    }

    //#endregion

    //#region Control
    private refreshDevicesAndSetups(): void {
        this.measurementDeviceService.refresh();
        this.measurementSetupRepository
            .getAll()
            .then((items) => (this.measurementSetupRepository.items = items))
            .catch(() => {});
    }

    private setMeasurementSetup(): void {
        if (this.measurementDevice) {
            // Copy the measurement device and remove capture devices that we dont want to use
            const duplicateDevice = MeasurementDevice.copy(this.measurementDevice);
            duplicateDevice.captureDevices = duplicateDevice.captureDevices.filter(
                (capDevice) => capDevice.id === this.captureDeviceId
            );

            let channelSelection;
            if (
                this.measurementAcquisitionService.activeSetup &&
                this.measurementDevice.id == this.measurementAcquisitionService.activeSetup.id &&
                this.measurementAcquisitionService.activeSetup.channelSelection
            ) {
                channelSelection = this.measurementAcquisitionService.activeSetup.channelSelection;
            } else {
                channelSelection = this.channelSelectionPresetService.getDefaultPreset(this.measurementDevice.frontEnd);
            }

            const connectedSetup = ConnectedMeasurementDeviceSetup.fromDevice(duplicateDevice, channelSelection);

            this.measurementAcquisitionService.setActiveSetup(connectedSetup);
        } else if (this.measurementSetup) {
            // Copy setup so we don't override it
            const duplicateSetup = ConnectedMeasurementDeviceSetup.copy(this.measurementSetup);

            // Remove all capture devices exept the one that is selected
            duplicateSetup.forEach((x) => {
                x.measurementDevice.captureDevices = x.measurementDevice.captureDevices.filter(
                    (x) => this.selectedCaptureDevice && x.id === this.selectedCaptureDevice.id
                );
            });

            this.measurementAcquisitionService.setActiveSetup(duplicateSetup);
        }
    }

    private getFrontEndNameForCaptureDevice(captureDevice: CaptureDevice): string {
        if (!this.measurementSetup) {
            if (this.measurementDevice) {
                return this.measurementDevice.frontEnd.name;
            }

            return "";
        }

        const setupInfo = this.measurementSetup.find((x) =>
            x.captureDevicesAsInSetup.some((y) => y.id === captureDevice.id)
        );
        return setupInfo && setupInfo.frontEndAsInSetup ? setupInfo.frontEndAsInSetup.name : "";
    }

    private disconnectMeasurementSetup(): void {
        this.setupId = undefined;
        this.frontEndId = undefined;
        this.captureDeviceId = undefined;
        this.measurementAcquisitionService.setActiveSetup(undefined);
    }

    private firmwareUpdate(): void {
        // We need to make sure that the device is not reserved
        this.measurementAcquisitionService.setActiveSetup(undefined);

        this.clientService.openFirmwareUpdateWindow();
    }

    private createSetup(): void {
        this.router.navigate("/MeasurementSetups");
    }
    //#endregion

    //#region Overrides

    protected onClientStatusChanged(status: ClientStatus): void {}

    //#endregion
}
